import { Loader2 } from "lucide-react"
import { useParams } from "react-router-dom"
import { useSessionRole } from "@/entities/session"
import { useScenarioDraft } from "@/features/edit-scenario-draft"
import { UnsavedChangesGuard } from "@/shared/hooks/use-unsaved-changes"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { Editor } from "@/widgets/scenario-editor"
import { Disclosure } from "@/shared/ui/disclosure"

/** Полноэкранный редактор сценария: потоки, холст, панель блока. */
export default function ScenarioEditorPage() {
	const { id = "" } = useParams()
	const { isModerator } = useSessionRole()
	const {
		draft,
		isLoading,
		refetch,
		isSaving,
		whenSaved,
		serverLoad,
		actions,
		saveError,
		recovery,
		reload,
	} = useScenarioDraft(id, isModerator && !!id)

	if (!isModerator) return <AccessDenied title="Сценарий" />

	if (isLoading) {
		return (
			<div className="fixed inset-0 z-40 flex items-center justify-center bg-background">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (!draft) {
		return (
			<div className="container mx-auto max-w-3xl px-3 py-6">
				<LoadErrorState message="Не удалось загрузить сценарий." onRetry={() => void refetch()} />
			</div>
		)
	}

	return (
		<>
			<UnsavedChangesGuard dirty={isSaving} />
			{saveError && (
				<div
					role="alert"
					className="fixed inset-x-4 top-4 z-[100] mx-auto max-w-xl space-y-3 rounded-2xl border border-destructive bg-card p-5 shadow-xl"
				>
					<h2 className="font-semibold">Изменения не сохранены</h2>
					<p className="text-sm">{saveError}</p>
					<Disclosure title="Несохранённые правки блоков">
						<pre className="max-h-40 overflow-auto whitespace-pre-wrap text-xs">
							{JSON.stringify(recovery.current, null, 2)}
						</pre>
					</Disclosure>
					<div className="flex flex-wrap gap-2">
						<Button
							variant="secondary"
							onClick={() => {
								const url = URL.createObjectURL(
									new Blob([JSON.stringify({ draft, edits: recovery.current }, null, 2)], {
										type: "application/json",
									}),
								)
								const link = document.createElement("a")
								link.href = url
								link.download = "scenario-recovery.json"
								link.click()
								setTimeout(() => URL.revokeObjectURL(url), 1000)
							}}
						>
							Скачать копию правок
						</Button>
						<Button onClick={() => void reload()}>Загрузить актуальную версию</Button>
					</div>
				</div>
			)}
			<Editor
				draft={draft}
				actions={actions}
				isSaving={isSaving}
				whenSaved={whenSaved}
				serverLoad={serverLoad}
			/>
		</>
	)
}
